// ── BCHNostr — Podcast episode page ───────────────────────────────
//
// One page per episode: artwork, metadata, a player that streams straight
// from the creator's own host, the BCH money rails, and a Nostr
// discussion thread.
//
// Two rules shape the whole file:
//
//   1. Audio is played from `episode.audioUrl` — the creator's host. We
//      never download, re-upload, cache, or proxy it, and we never copy
//      audio into a Nostr event.
//   2. Everything in `episode` came out of an RSS feed and is therefore
//      untrusted. lib/podcast.ts already stripped HTML and rejected
//      non-http(s) URLs; nothing here re-introduces raw markup.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Mic2, Play, Pause, ArrowLeft, Calendar, Clock, Rocket, MessageCircle,
  Share2, ExternalLink, Loader2, Send, Check, Hash, Heart,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrProfile, useNostrQuery } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useBCHAddress } from '../hooks/useBCHAddress';
import { useBCHPodcast, usePodcastFeed } from '../hooks/usePodcast';
import {
  findEpisodeBySlug, episodeSlug, episodeExternalId, podcastPath,
  formatEpisodeDuration, formatEpisodeDate, recordEpisodeView, recordPlayClick,
  buildShareText, PODCAST_COMMENT_KIND,
  type PodcastShow, type PodcastEpisode,
} from '../lib/podcast';
import BCHTipButton from '../components/BCHTipButton';
import PodcastBoostModal from '../components/PodcastBoostModal';
import { EmptyState, FollowPodcastButton, FeedNotice } from './PodcastPage';
import { timeAgo } from '../lib/utils';
import UserBadges from '../components/UserBadges';

// ── Audio player ──────────────────────────────────────────────────
//
// A plain <audio> element pointed at the creator's own URL. No proxy, no
// re-hosting, no service worker interception. If their host is down, the
// browser reports it and we show a link out to their episode page.

function AudioPlayer({
  show, episode,
}: { show: PodcastShow; episode: PodcastEpisode }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const countedRef = useRef(false);

  const handleToggle = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      // A play CLICK, recorded once per page view. This is explicitly not
      // a "listen" — see the note under the player.
      if (!countedRef.current) {
        countedRef.current = true;
        recordPlayClick(show.pubkey, episode.guid);
      }
      el.play().catch(() => setFailed(true));
    } else {
      el.pause();
    }
  }, [show.pubkey, episode.guid]);

  return (
    <div className="rounded-xl border border-border bg-card p-3 space-y-2">
      <div className="flex items-center gap-3">
        <button
          onClick={handleToggle}
          className="w-12 h-12 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
          aria-label={playing ? 'Pause episode' : 'Play episode'}
        >
          {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        <div className="min-w-0 flex-1">
          {/* Native controls give us seeking, speed, and AirPlay for free,
              and keep playback entirely between the browser and the host. */}
          <audio
            ref={audioRef}
            src={episode.audioUrl}
            controls
            preload="none"
            className="w-full h-9"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            onError={() => { setFailed(true); setPlaying(false); }}
          />
        </div>
      </div>

      {failed && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400">
          This episode’s audio couldn’t be loaded from the podcast’s host.
          {episode.link && (
            <>
              {' '}
              <a href={episode.link} target="_blank" rel="noopener noreferrer" className="underline">
                Open it on the podcast’s site
              </a>.
            </>
          )}
        </p>
      )}

      <p className="text-[10px] text-muted-foreground/80 leading-relaxed">
        Streaming directly from the podcast’s own host. BCHNostr doesn’t host or
        proxy podcast audio.
      </p>
    </div>
  );
}

// ── Discussion ────────────────────────────────────────────────────
//
// NIP-22 comments (kind 1111) rooted on the episode's NIP-73 external id
// (`podcast:item:guid:<guid>`). That makes the thread portable: any client
// that understands NIP-22 + NIP-73 sees the same conversation, and nothing
// about the audio is duplicated into Nostr.

function useEpisodeComments(guid: string | undefined) {
  const externalId = guid ? episodeExternalId(guid) : '';
  const { events, loading, refetch } = useNostrQuery({
    filters: externalId
      ? [{ kinds: [PODCAST_COMMENT_KIND], '#I': [externalId], limit: 200 }]
      : [],
    enabled: !!externalId,
    allRelays: true,
  });

  const comments = useMemo(
    () => [...events].sort((a, b) => b.created_at - a.created_at),
    [events],
  );

  return { comments, loading, refetch };
}

function CommentRow({ event }: { event: { id: string; pubkey: string; content: string; created_at: number } }) {
  const { profile } = useNostrProfile(event.pubkey);
  const name = profile?.display_name || profile?.name || event.pubkey.slice(0, 8);

  return (
    <div className="flex items-start gap-2.5 px-4 py-3 border-b border-border">
      <Link to={`/profile/${event.pubkey}`} className="shrink-0">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary/10" />
        )}
      </Link>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Link to={`/profile/${event.pubkey}`} className="text-xs font-semibold hover:underline truncate">
            {name}
          </Link>
          <UserBadges pubkey={event.pubkey} size="sm" showTooltip={false} />
          <span className="text-[10px] text-muted-foreground">{timeAgo(event.created_at)}</span>
        </div>
        {/* Plain text only — never dangerouslySetInnerHTML on user content. */}
        <p className="text-sm mt-0.5 whitespace-pre-wrap break-words">{event.content}</p>
      </div>
      <div className="shrink-0 no-navigate">
        <BCHTipButton recipientPubkey={event.pubkey} recipientProfile={profile} eventId={event.id} dropdownAlign="right" />
      </div>
    </div>
  );
}

function DiscussionSection({
  show, episode,
}: { show: PodcastShow; episode: PodcastEpisode }) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const { comments, loading, refetch } = useEpisodeComments(episode.guid);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');

  const handlePost = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    const content = draft.trim();
    if (!content || posting) return;

    setPosting(true);
    setError('');
    const externalId = episodeExternalId(episode.guid);
    try {
      await publish({
        kind: PODCAST_COMMENT_KIND,
        content,
        tags: [
          // Root scope (uppercase) — the episode as a NIP-73 external id.
          ['I', externalId],
          ['K', 'podcast:item:guid'],
          // Parent (lowercase) — same as the root for a top-level comment.
          ['i', externalId],
          ['k', 'podcast:item:guid'],
          // So the podcaster is notified of discussion about their episode.
          ['p', show.pubkey],
          // Discovery + a human-readable fallback for clients that don't
          // resolve the external id.
          ['t', 'bch-podcast-comment'],
          ['alt', `Comment on the podcast episode "${episode.title}"`],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setDraft('');
      refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 px-4 py-3 border-y border-border">
        <MessageCircle className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-bold flex-1">Discussion</h3>
        <span className="text-[11px] text-muted-foreground">
          {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
        </span>
      </div>

      {user ? (
        <div className="px-4 py-3 border-b border-border">
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder={`What did you think of "${episode.title}"?`}
            rows={3}
            maxLength={2000}
            className="w-full bg-muted rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-[10px] text-muted-foreground">
              Posted to Nostr as a comment on this episode.
            </p>
            <button
              onClick={handlePost}
              disabled={!draft.trim() || posting}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
            >
              {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {posting ? 'Posting' : 'Reply'}
            </button>
          </div>
          {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
        </div>
      ) : (
        <div className="px-4 py-4 border-b border-border text-center">
          <button
            onClick={() => setShowLoginDialog(true)}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold"
          >
            Log in to join the discussion
          </button>
        </div>
      )}

      {loading && comments.length === 0 ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-10 px-4">
          <MessageCircle className="w-8 h-8 mx-auto text-muted-foreground/25 mb-2" />
          <p className="text-xs text-muted-foreground">No comments yet. Start the conversation.</p>
        </div>
      ) : (
        comments.map(c => <CommentRow key={c.id} event={c} />)
      )}
    </div>
  );
}

// ── Share ─────────────────────────────────────────────────────────

function ShareButton({ show, episode }: { show: PodcastShow; episode: PodcastEpisode }) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const [state, setState] = useState<'idle' | 'sharing' | 'shared'>('idle');

  const handleShare = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    if (state !== 'idle') return;
    setState('sharing');

    const appUrl = `${window.location.origin}/podcasts/${show.pubkey}/${episodeSlug(episode.guid)}`;
    try {
      await publish({
        kind: 1,
        // Podcast name, episode title, the creator's episode URL, and a link
        // back to the BCHNostr page. The audio itself is never duplicated
        // into the event — just the pointers.
        content: buildShareText(show, episode, appUrl),
        tags: [
          ['t', 'bchnostr'],
          ['t', 'podcast'],
          ['i', episodeExternalId(episode.guid)],
          ['k', 'podcast:item:guid'],
          ['p', show.pubkey],
          ['r', appUrl],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setState('shared');
      setTimeout(() => setState('idle'), 3000);
    } catch {
      setState('idle');
    }
  };

  return (
    <button
      onClick={handleShare}
      disabled={state === 'sharing'}
      className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary/40 disabled:opacity-60"
    >
      {state === 'sharing' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : state === 'shared' ? <Check className="w-3.5 h-3.5 text-primary" />
        : <Share2 className="w-3.5 h-3.5" />}
      {state === 'shared' ? 'Shared' : 'Share'}
    </button>
  );
}

// ── Reaction ──────────────────────────────────────────────────────

function LikeButton({ episode }: { episode: PodcastEpisode }) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const externalId = episodeExternalId(episode.guid);
  const [liked, setLiked] = useState(false);

  const { events } = useNostrQuery({
    filters: [{ kinds: [7], '#i': [externalId], limit: 200 }],
    allRelays: true,
  });

  const count = useMemo(() => new Set(events.map(e => e.pubkey)).size, [events]);
  const alreadyLiked = liked || (!!user && events.some(e => e.pubkey === user.pubkey));

  const handleLike = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    if (alreadyLiked) return;
    setLiked(true);
    try {
      // A NIP-25 reaction on a NIP-73 external id — the same shape the
      // comments use, so reactions and comments address one identity.
      await publish({
        kind: 7,
        content: '+',
        tags: [
          ['i', externalId],
          ['k', 'podcast:item:guid'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch {
      setLiked(false);
    }
  };

  return (
    <button
      onClick={handleLike}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-xs font-semibold transition-colors ${
        alreadyLiked
          ? 'border-rose-500/40 bg-rose-500/10 text-rose-500'
          : 'border-border text-muted-foreground hover:text-rose-500 hover:border-rose-500/40'
      }`}
    >
      <Heart className={`w-3.5 h-3.5 ${alreadyLiked ? 'fill-current' : ''}`} />
      {count > 0 ? count : 'Like'}
    </button>
  );
}

// ── Page ──────────────────────────────────────────────────────────

export default function EpisodePage() {
  const { pubkey: rawPubkey, slug } = useParams<{ pubkey: string; slug: string }>();
  const pubkey = useMemo(
    () => (rawPubkey && /^[0-9a-f]{64}$/i.test(rawPubkey) ? rawPubkey.toLowerCase() : undefined),
    [rawPubkey],
  );

  const { podcast, loading: showLoading } = useBCHPodcast(pubkey);
  const { profile } = useNostrProfile(pubkey);
  const { episodes, loading: feedLoading, error } = usePodcastFeed(podcast?.feedUrl);
  const { address: creatorAddress } = useBCHAddress(pubkey);
  const [boostOpen, setBoostOpen] = useState(false);

  const episode = useMemo(
    () => (slug ? findEpisodeBySlug(episodes, slug) : null),
    [episodes, slug],
  );

  useEffect(() => {
    if (pubkey && episode) recordEpisodeView(pubkey, episode.guid);
  }, [pubkey, episode?.guid]);

  if (!pubkey || !slug) {
    return <EmptyState title="Episode not found" body="That episode link isn’t valid." />;
  }

  if ((showLoading && !podcast) || (feedLoading && !episode)) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!podcast) {
    return (
      <EmptyState
        title="Podcast not found"
        body="This account hasn’t connected a podcast."
        action={{ to: '/podcasts', label: 'Browse podcasts' }}
      />
    );
  }

  if (!episode) {
    return (
      <div>
        {error && <FeedNotice error={error} hasEpisodes={episodes.length > 0} />}
        <EmptyState
          title="Episode not available"
          body="This episode isn’t in the podcast’s feed anymore. It may have been removed by the creator."
          action={{ to: podcastPath(pubkey), label: `Back to ${podcast.name}` }}
        />
      </div>
    );
  }

  const art = episode.image || podcast.image;
  const duration = formatEpisodeDuration(episode.duration);
  const date = formatEpisodeDate(episode.publishedAt);
  const tipAddress = podcast.bch || creatorAddress || '';

  return (
    <div className="pb-8">
      {/* ── Header ── */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3 flex items-center gap-2">
        <Link to={podcastPath(pubkey)} className="p-1.5 -ml-1.5 rounded-lg hover:bg-muted text-muted-foreground">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="text-sm font-bold truncate">{episode.title}</h1>
          <p className="text-[11px] text-muted-foreground truncate">{podcast.name}</p>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* ── Episode identity ── */}
        <div className="flex items-start gap-3.5">
          {art ? (
            <img src={art} alt="" className="w-24 h-24 rounded-xl object-cover border border-border shrink-0" />
          ) : (
            <div className="w-24 h-24 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Mic2 className="w-8 h-8 text-primary/50" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <Link
              to={podcastPath(pubkey)}
              className="text-[11px] font-semibold text-primary hover:underline flex items-center gap-1"
            >
              <Mic2 className="w-3 h-3" /> {podcast.name}
            </Link>
            <h2 className="text-base font-extrabold leading-snug mt-0.5">{episode.title}</h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-muted-foreground">
              {episode.episodeNumber !== null && (
                <span className="flex items-center gap-1">
                  <Hash className="w-2.5 h-2.5" />
                  {episode.seasonNumber !== null && `S${episode.seasonNumber} · `}
                  Episode {episode.episodeNumber}
                </span>
              )}
              {date && <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{date}</span>}
              {duration && <span className="flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{duration}</span>}
            </div>
          </div>
        </div>

        {/* ── Player ── */}
        <AudioPlayer show={podcast} episode={episode} />

        {/* ── Actions ── */}
        <div className="flex flex-wrap items-center gap-2">
          {tipAddress && (
            <div className="no-navigate">
              <BCHTipButton recipientPubkey={podcast.pubkey} recipientProfile={profile} />
            </div>
          )}
          <button
            onClick={() => setBoostOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-semibold hover:opacity-90"
          >
            <Rocket className="w-3.5 h-3.5" /> Boost
          </button>
          <a
            href="#episode-discussion"
            className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary/40"
          >
            <MessageCircle className="w-3.5 h-3.5" /> Discuss
          </a>
          <FollowPodcastButton pubkey={podcast.pubkey} />
          <LikeButton episode={episode} />
          <ShareButton show={podcast} episode={episode} />
          {episode.link && (
            <a
              href={episode.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary/40"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Episode page
            </a>
          )}
        </div>

        {/* ── Description ── */}
        {episode.description && (
          <div>
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1.5">
              About this episode
            </h3>
            {/* Already stripped to plain text at the RSS boundary. */}
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{episode.description}</p>
          </div>
        )}
      </div>

      {/* ── Discussion ── */}
      <div id="episode-discussion">
        <DiscussionSection show={podcast} episode={episode} />
      </div>

      {boostOpen && (
        <PodcastBoostModal
          podcastPubkey={podcast.pubkey}
          podcastName={podcast.name}
          episodeGuid={episode.guid}
          episodeTitle={episode.title}
          onClose={() => setBoostOpen(false)}
        />
      )}
    </div>
  );
}
